import apiClient from "@/services/api-client";
import type {
  AuditLogListResponse,
  PlatformAuditEventListResponse,
  DefaultRoleTemplateItem,
  DefaultRoleTemplatePayload,
  PermissionItem,
  PermissionPayload,
  PlatformUserCreatePayload,
  PlatformUserListQuery,
  PlatformUserListResponse,
  PlatformUserUpdatePayload,
  RoleItem,
  RolePayload,
} from "@/types";

export const governanceService = {
  listPlatformUsers: async (params: PlatformUserListQuery) => {
    const response = await apiClient.get<PlatformUserListResponse>(
      "/governance/platform-users",
      { params },
    );
    return response.data;
  },
  createPlatformUser: async (payload: PlatformUserCreatePayload) => {
    const response = await apiClient.post(
      "/governance/platform-users",
      payload,
    );
    return response.data;
  },
  updatePlatformUser: async (
    id: string,
    payload: PlatformUserUpdatePayload,
  ) => {
    const response = await apiClient.patch(
      `/governance/platform-users/${id}`,
      payload,
    );
    return response.data;
  },
  listAuditLogs: async (params: {
    page?: number;
    limit?: number;
    tenantId?: string;
    userId?: string;
    entity?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get<AuditLogListResponse>(
      "/governance/audit-logs",
      { params },
    );
    return response.data;
  },
  listPlatformAuditEvents: async (params: {
    page?: number;
    limit?: number;
    actorEmail?: string;
    action?: string;
    outcome?: string;
    tenantId?: string;
    correlationId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get<PlatformAuditEventListResponse>(
      "/governance/platform-audit-events",
      { params },
    );
    return response.data;
  },
  listPermissions: async () => {
    const response = await apiClient.get<PermissionItem[]>(
      "/governance/permissions",
    );
    return response.data;
  },
  createPermission: async (payload: PermissionPayload) => {
    const response = await apiClient.post<PermissionItem>(
      "/governance/permissions",
      payload,
    );
    return response.data;
  },
  updatePermission: async (id: string, payload: Partial<PermissionPayload>) => {
    const response = await apiClient.patch<PermissionItem>(
      `/governance/permissions/${id}`,
      payload,
    );
    return response.data;
  },
  deletePermission: async (id: string) => {
    const response = await apiClient.delete(`/governance/permissions/${id}`);
    return response.data;
  },
  listRoles: async (params?: {
    tenantId?: string;
    includeGlobal?: boolean;
  }) => {
    const response = await apiClient.get<RoleItem[]>("/governance/roles", {
      params,
    });
    return response.data;
  },
  createRole: async (payload: RolePayload) => {
    const response = await apiClient.post<RoleItem>(
      "/governance/roles",
      payload,
    );
    return response.data;
  },
  updateRole: async (id: string, payload: Partial<RolePayload>) => {
    const response = await apiClient.patch<RoleItem>(
      `/governance/roles/${id}`,
      payload,
    );
    return response.data;
  },
  deleteRole: async (id: string) => {
    const response = await apiClient.delete(`/governance/roles/${id}`);
    return response.data;
  },
  syncTenantPermissions: async (tenantId: string) => {
    const response = await apiClient.post<{
      success: boolean;
      tenantId: string;
      tenantName: string;
      message: string;
      roles: Array<{
        name: string;
        id: string;
        permissionCount: number;
        permissions: string[];
      }>;
    }>(`/governance/tenants/${tenantId}/sync-permissions`);
    return response.data;
  },

  // ─── Default Role Templates ──────────────────────────────────────────

  listDefaultRoleTemplates: async () => {
    const response = await apiClient.get<DefaultRoleTemplateItem[]>(
      "/governance/role-templates",
    );
    return response.data;
  },

  getDefaultRoleTemplate: async (id: string) => {
    const response = await apiClient.get<DefaultRoleTemplateItem>(
      `/governance/role-templates/${id}`,
    );
    return response.data;
  },

  createDefaultRoleTemplate: async (payload: DefaultRoleTemplatePayload) => {
    const response = await apiClient.post<DefaultRoleTemplateItem>(
      "/governance/role-templates",
      payload,
    );
    return response.data;
  },

  updateDefaultRoleTemplate: async (
    id: string,
    payload: Partial<DefaultRoleTemplatePayload>,
  ) => {
    const response = await apiClient.patch<DefaultRoleTemplateItem>(
      `/governance/role-templates/${id}`,
      payload,
    );
    return response.data;
  },

  deleteDefaultRoleTemplate: async (id: string) => {
    const response = await apiClient.delete(`/governance/role-templates/${id}`);
    return response.data;
  },
};
